import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { SkeletonRows } from '../components/ui/Skeleton';
import {
  FlowDialog,
  FlowLayout,
  PublicLayout,
  RequireAuth,
  RouteFocus,
  SkipLink,
  TabLayout,
} from './Layouts';
import type { FlowState } from './hooks';
import { PwaUpdater } from './PwaUpdater';
import { SessionEffects } from './SessionEffects';

const SetupScreen = lazy(() => import('../screens/auth/SetupScreen'));
const InviteScreen = lazy(() => import('../screens/auth/InviteScreen'));
const SignInScreen = lazy(() => import('../screens/auth/SignInScreen'));
const JoinGroupScreen = lazy(() => import('../screens/auth/JoinGroupScreen'));
const HomeScreen = lazy(() => import('../screens/home/HomeScreen'));
const PersonScreen = lazy(() => import('../screens/home/PersonScreen'));
const GroupsScreen = lazy(() => import('../screens/groups/GroupsScreen'));
const NewGroupScreen = lazy(() => import('../screens/groups/NewGroupScreen'));
const GroupScreen = lazy(() => import('../screens/groups/GroupScreen'));
const GroupSettingsScreen = lazy(() => import('../screens/groups/GroupSettingsScreen'));
const ExpenseFormScreen = lazy(() => import('../screens/expenses/ExpenseFormScreen'));
const ExpenseDetailScreen = lazy(() => import('../screens/expenses/ExpenseDetailScreen'));
const SettleScreen = lazy(() => import('../screens/settle/SettleScreen'));
const ActivityScreen = lazy(() => import('../screens/activity/ActivityScreen'));
const InboxScreen = lazy(() => import('../screens/notifications/InboxScreen'));
const NotificationSettingsScreen = lazy(
  () => import('../screens/notifications/NotificationSettingsScreen'),
);
const MeScreen = lazy(() => import('../screens/me/MeScreen'));
const InvitesScreen = lazy(() => import('../screens/me/InvitesScreen'));
const AdminScreen = lazy(() => import('../screens/admin/AdminScreen'));
const NotFoundScreen = lazy(() => import('../screens/misc/NotFoundScreen'));

function Loading() {
  return (
    <div className="px-5 pt-16">
      <SkeletonRows count={4} />
    </div>
  );
}

function S({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/** Flows that open as dialogs over the current page on desktop (DESIGN 3.3). */
const FLOW_ROUTES: { path: string; element: ReactNode }[] = [
  { path: '/groups/new', element: <NewGroupScreen /> },
  { path: '/groups/:id/settings', element: <GroupSettingsScreen /> },
  { path: '/groups/:id/expenses/new', element: <ExpenseFormScreen /> },
  { path: '/expenses/:id/edit', element: <ExpenseFormScreen /> },
  { path: '/settle', element: <SettleScreen /> },
];

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const background = (location.state as FlowState | null)?.background;

  return (
    <>
      <SkipLink />
      <RouteFocus />
      <PwaUpdater />
      <SessionEffects />
      <Routes location={background ?? location}>
        <Route element={<PublicLayout />}>
          <Route
            path="/setup"
            element={
              <S>
                <SetupScreen />
              </S>
            }
          />
          <Route
            path="/invite/:code"
            element={
              <S>
                <InviteScreen />
              </S>
            }
          />
          <Route
            path="/signin"
            element={
              <S>
                <SignInScreen />
              </S>
            }
          />
          <Route
            path="/join/:token"
            element={
              <S>
                <JoinGroupScreen />
              </S>
            }
          />
        </Route>
        <Route element={<RequireAuth />}>
          <Route element={<TabLayout />}>
            <Route
              index
              element={
                <S>
                  <HomeScreen />
                </S>
              }
            />
            <Route
              path="/groups"
              element={
                <S>
                  <GroupsScreen />
                </S>
              }
            />
            <Route
              path="/groups/:id"
              element={
                <S>
                  <GroupScreen />
                </S>
              }
            />
            <Route
              path="/people/:id"
              element={
                <S>
                  <PersonScreen />
                </S>
              }
            />
            <Route
              path="/activity"
              element={
                <S>
                  <ActivityScreen />
                </S>
              }
            />
            <Route
              path="/me"
              element={
                <S>
                  <MeScreen />
                </S>
              }
            />
          </Route>
          <Route element={<FlowLayout />}>
            {FLOW_ROUTES.map((r) => (
              <Route key={r.path} path={r.path} element={<S>{r.element}</S>} />
            ))}
            <Route
              path="/expenses/:id"
              element={
                <S>
                  <ExpenseDetailScreen />
                </S>
              }
            />
            <Route
              path="/notifications"
              element={
                <S>
                  <InboxScreen />
                </S>
              }
            />
            <Route
              path="/me/notifications"
              element={
                <S>
                  <NotificationSettingsScreen />
                </S>
              }
            />
            <Route
              path="/me/invites"
              element={
                <S>
                  <InvitesScreen />
                </S>
              }
            />
            <Route
              path="/admin"
              element={
                <S>
                  <AdminScreen />
                </S>
              }
            />
            <Route
              path="/admin/invites"
              element={
                <S>
                  <AdminScreen />
                </S>
              }
            />
            <Route
              path="/admin/users"
              element={
                <S>
                  <AdminScreen />
                </S>
              }
            />
            <Route
              path="*"
              element={
                <S>
                  <NotFoundScreen />
                </S>
              }
            />
          </Route>
        </Route>
      </Routes>
      {background && (
        <Routes>
          <Route element={<RequireAuth />}>
            {FLOW_ROUTES.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  <FlowDialog onClose={() => void navigate(-1)}>
                    <S>{r.element}</S>
                  </FlowDialog>
                }
              />
            ))}
          </Route>
        </Routes>
      )}
    </>
  );
}
